// src/components/Atelier3/Activite3/GuideModal.js
import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { X, HelpCircle, Search, Eye, Download, Target, AlertTriangle, Route, ArrowRight, CheckCircle } from 'lucide-react';

const GuideModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const steps = [
    {
      title: "Explorer les Chemins d'Attaque",
      description: "Parcourez la liste des chemins d'attaque générés depuis les activités précédentes, incluant les sources de risque, objectifs visés et événements redoutés.",
      icon: <Target size={20} className="text-blue-500" />
    },
    {
      title: "Utiliser les Filtres de Recherche",
      description: "Utilisez la barre de recherche pour filtrer rapidement les chemins d'attaque par source de risque, objectif ou événement redouté.",
      icon: <Search size={20} className="text-green-500" />
    },
    {
      title: "Trier les Données",
      description: "Cliquez sur les en-têtes des colonnes pour trier les chemins d'attaque par référence, source de risque, objectif ou valeur métier.",
      icon: <ArrowRight size={20} className="text-purple-500" />
    },
    {
      title: "Visualiser les Chemins d'Attaque",
      description: "Cliquez sur 'Voir' pour visualiser graphiquement chaque chemin d'attaque avec ses éléments interconnectés et parties prenantes concernées.",
      icon: <Eye size={20} className="text-orange-500" />
    },
    {
      title: "Explorer les Détails",
      description: "Cliquez sur 'Détails' pour consulter les informations complètes d'un chemin d'attaque et visualiser graphiquement depuis cette même fenêtre.",
      icon: <AlertTriangle size={20} className="text-red-500" />
    },
    {
      title: "Exporter les Données",
      description: "Exportez la liste des chemins d'attaque au format CSV dans la langue de votre choix pour analyse approfondie ou archivage.",
      icon: <Download size={20} className="text-teal-500" />
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden mb-6"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 flex justify-between items-center">
        <div className="flex items-center">
          <HelpCircle size={24} className="text-white mr-3" />
          <h2 className="text-xl font-bold text-white">Guide des Chemins d'Attaque</h2>
        </div>
        <button
          onClick={onClose}
          className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors duration-200"
          aria-label="Fermer le guide"
        >
          <X size={24} />
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="mb-6">
          <p className="text-gray-600 text-center">
            Cette activité vous permet d'explorer et de synchroniser les chemins d'attaque identifiés dans les étapes précédentes de votre analyse EBios RM.
          </p>
        </div>

        <div className="space-y-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="flex"
            >
              <div className="flex-shrink-0 mr-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-600 font-bold">
                  {index + 1}
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  {step.icon}
                  <h3 className="text-lg font-semibold text-gray-800 ml-2">{step.title}</h3>
                </div>
                <p className="text-gray-600">{step.description}</p>
              </div>
            </motion.div>
          ))}

          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md mt-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <Route className="h-5 w-5 text-blue-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Conseil</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p>
                    Commencez par examiner les chemins d'attaque les plus critiques en vous concentrant sur ceux qui impactent les valeurs métier les plus importantes
                    de votre organisation. La visualisation graphique vous aidera à mieux comprendre les interdépendances entre les éléments de risque.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Objectifs Atteints</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>
                    À la fin de cette activité, vous aurez une vue d'ensemble synchronisée de tous vos chemins d'attaque,
                    avec la possibilité de les explorer visuellement et de les exporter pour documentation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default GuideModal;
